import { useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, LinkButton } from '@evertrue/et-components';
import EnrichmentModalHeader from './enrichment-modal-header';
import EnrichmentList from './enrichment-list';
import { formatNetWorths } from './utils/index';

/**
 * Modal for viewing and editing Net Worth data.
 * Uses the same EnrichmentList pattern as the existing WealthIndicatorsModal
 * for consistent delete/restore functionality.
 */
const NetWorthModal = ({ isOpen, closeModal, contactName = '', contactId, netWorths = [], onDelete, updateDate }) => {
  const [showRejected, setShowRejected] = useState(false);

  // Format net worths for display (same as WealthIndicators)
  const formattedNetWorths = formatNetWorths(netWorths);
  const activeRecords = formattedNetWorths.filter((record) => !record.rejected);
  const rejectedRecords = formattedNetWorths.filter((record) => record.rejected);
  const hasRejectedRecords = rejectedRecords.length > 0;

  return (
    <Modal isOpen={isOpen} closeModal={closeModal} size="500px">
      <EnrichmentModalHeader
        headerText={`${contactName}'s Net Worth`}
        closeModal={closeModal}
        updateDate={updateDate}
      />

      <div className="profile-enrichment--more-modal">
        {activeRecords.length > 0 ? (
          <EnrichmentList
            title="Net Worth"
            list={activeRecords}
            accessor="value"
            limit={activeRecords.length}
            className="profile--enrichment-wealth--networth"
            contactId={contactId}
            onDelete={onDelete}
            showActions={true}
          />
        ) : (
          <div className="net-worth-modal--empty">No net worth records available</div>
        )}

        {hasRejectedRecords && (
          <LinkButton
            className="net-worth-modal--toggle-rejected"
            onClick={() => setShowRejected(!showRejected)}
            title=""
          >
            {showRejected ? 'Hide deleted' : 'See deleted'}
          </LinkButton>
        )}

        {showRejected && rejectedRecords.length > 0 && (
          <div style={{ marginTop: '15px' }}>
            <EnrichmentList
              title="Net Worth"
              list={rejectedRecords}
              accessor="value"
              limit={rejectedRecords.length}
              className="profile--enrichment-wealth--networth"
              contactId={contactId}
              onDelete={onDelete}
              showActions={true}
            />
          </div>
        )}
      </div>
    </Modal>
  );
};

NetWorthModal.propTypes = {
  isOpen: PropTypes.bool,
  closeModal: PropTypes.func,
  contactName: PropTypes.string,
  contactId: PropTypes.number,
  /** Raw net worth data from wealth_attributes (same as WealthIndicators receives) */
  netWorths: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      net_worth: PropTypes.number,
      updated_at: PropTypes.string,
      rejected: PropTypes.bool,
    })
  ),
  /** Callback when delete/restore completes (typically triggers data refetch) */
  onDelete: PropTypes.func,
  /** Formatted date string for the "Data was last added" subheader */
  updateDate: PropTypes.string,
};

export default NetWorthModal;
